import {
  Child529Plan,
  CollegeCostPreset,
  DebtKind,
  DebtObligation,
  FinancialPlanData,
  FinancialPlanProfile,
  IncomePhase,
  LgcSectionId,
} from '../types/financialPlan';
import {
  computeInvestablePoolAtAge,
  initializeAccountContributionPercents,
} from './lgcAccountAllocation';
import { formatISODateLocal, isValidISODate, resolvePlanAges } from './planDates';

export const COLLEGE_COST_PRESETS: Record<CollegeCostPreset, { label: string; annualCost: number }> = {
  public_in_state: { label: 'Public in-state', annualCost: 28000 },
  public_out_state: { label: 'Public out-of-state', annualCost: 45000 },
  private: { label: 'Private', annualCost: 62000 },
  custom: { label: 'Custom', annualCost: 0 },
};

export const IRS_LIMITS_2026 = {
  rothIra: 7000,
  rothIraCatchUp: 8000,
  /** Legacy key — employee deferral; kept for reference in limits footnote */
  k401: 23500,
  /** SEP IRA employer contribution cap (§415 annual addition limit) */
  sepIra: 69000,
  hsaIndividual: 4300,
  hsaFamily: 8550,
};

/** Primary tax-deferred account for LCTF — stored as `accounts.k401` for plan compatibility */
export const BUSINESS_SEP_IRA = {
  title: 'SEP IRA',
  businessName: "Leiper's Creek Timber Frames",
  description: 'Self-managed SEP IRA through the business',
} as const;

export const ACCOUNT_DISPLAY_LABELS: Record<
  keyof import('../types/financialPlan').FinancialPlanAccounts,
  string
> = {
  k401: BUSINESS_SEP_IRA.title,
  rothIra: 'Roth IRA',
  hsa: 'HSA',
  taxable: 'Taxable brokerage',
};

export const SECTION_LABELS: Record<LgcSectionId, string> = {
  summary: 'Plan Summary',
  timeline: 'Growth Timeline',
  debug: 'Plan Debug',
  profile: 'Profile & Assumptions',
  current: 'Current Finances',
  income: 'Income Phases',
  debt: 'Debt',
  retirement: 'Retirement Spending',
  college: 'Children & Education',
  health: 'Health & Insurance',
  lifestyle: 'Lifestyle',
  accounts: 'Retirement Accounts',
};

export const SECTION_HINTS: Partial<Record<LgcSectionId, string>> = {
  summary:
    'Plan health at a glance: required income vs your inputs, phased affordability, retirement target, and monthly spend/save breakdown.',
  timeline:
    'Interactive wealth projection from today through plan end. Scrub or pin an age to see required save/spend, income, and portfolio paths.',
  debug:
    'Year-by-year audit table. Total configured path = Accounts contributions only. Total goal path = configured plus Goal path extra/mo (when retirement catch-up or yearly gap exists). When Goal path extra/mo is $0 every year, both totals match.',
  profile: 'Historical inflation ~3%. Moderate portfolio return often modeled at 6–8% nominal. Set birthdays so ages advance automatically; plan start date anchors timeline calendar years.',
  current: 'Monthly budget and tithing. Gross income by age is set under Income Phases.',
  income: 'All household income by age. Use one phase for steady income, or several for career growth.',
  debt: 'Mortgage, credit card, and personal loans. When paid off, payments stop and freed cash can go to savings.',
  retirement: 'Safe withdrawal rate of 3.5–4.5% is common for 30-year retirements.',
  college: '529 earnings grow tax-free when used for qualified education expenses. K-12 costs are cash outflows in school years.',
  accounts: 'Tax-advantaged order: SEP IRA (employer) → HSA → Roth IRA → taxable brokerage. Contributions default to % of investable income after primary commitments.',
};

export const DEBT_KIND_LABELS: Record<DebtKind, string> = {
  mortgage: 'Mortgage',
  credit_card: 'Credit card',
  personal: 'Personal loan',
  other: 'Other',
};

export const FIELD_HINTS: Record<string, string> = {
  safeWithdrawalRatePct: 'Used to derive corpus from spending and to compute SWR maximum income below. 4% on $4M ≈ $160k/yr at retirement start.',
  rothIraContribution: `2026 Roth IRA max: $${IRS_LIMITS_2026.rothIra.toLocaleString()} ($${IRS_LIMITS_2026.rothIraCatchUp.toLocaleString()} age 50+).`,
  k401Contribution: `${BUSINESS_SEP_IRA.description} (${BUSINESS_SEP_IRA.businessName}). 2026 max employer contribution: $${IRS_LIMITS_2026.sepIra.toLocaleString()} (lesser of 25% of net self-employment income or IRS cap).`,
  hsaContribution: `2026 HSA family max: $${IRS_LIMITS_2026.hsaFamily.toLocaleString()}.`,
  inflationPct: 'Used to inflate retirement spending and college costs to future dollars.',
  effectiveTaxRatePct:
    'Blended federal, state & payroll burden. Grosses up net monthly need to pre-tax — at 24%, $10k/mo net ≈ $13.2k/mo gross.',
  birthDate:
    'Your date of birth. When set, your plan age updates automatically as time passes — no need to edit age each year.',
  spouseBirthDate: 'Spouse date of birth. Age is computed the same way as yours.',
  planStartDate:
    'The calendar date your projection begins. Timeline years are anchored here (or to your birthday when set).',
  childBirthDate: 'Child date of birth. School and college timing follow their age automatically.',
  tithingPct:
    'Percent of after-tax (take-home) income for tithing or charitable giving. Calculated from gross income and your effective tax rate.',
  preMedicareMonthlyPremium:
    'Estimated monthly cost for private or Marketplace coverage before Medicare at 65. Often $1,500–$2,500+ per couple until Medicare kicks in.',
  yearsOfCoverageNeeded:
    'How many years you expect to self-fund coverage before Medicare — e.g. retire at 62, Medicare at 65 → 3 years.',
  hsaAnnualContribution: `Planned annual HSA funding counted toward your health reserve. 2026 family max: $${IRS_LIMITS_2026.hsaFamily.toLocaleString()}.`,
  deductibleBufferTarget:
    'One-time cash cushion for deductibles, copays, and out-of-pocket costs your plan does not cover upfront.',
  incomePhaseGross:
    'Annual gross in today\'s dollars for this phase. Future years inflate by your plan inflation rate.',
  incomePhaseGrowth:
    'Year-over-year raise within a phase, in today\'s dollars (starts the year after phase start age).',
  debtMonthlyPayment: 'Minimum or planned monthly payment. Stops when balance reaches zero.',
  k12PrimaryCost: 'Annual K-12 cost in today\'s dollars while child is in primary grades.',
  k12HighSchoolCost: 'Annual cost in today\'s dollars while child is in high school.',
};

export function newChildId(): string {
  return `child-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function newGoalLineId(): string {
  return `goal-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function newSpendingCategoryId(): string {
  return `spend-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function newBudgetCategoryId(): string {
  return `budget-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function newIncomePhaseId(): string {
  return `phase-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function newDebtId(): string {
  return `debt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export const DEFAULT_K12_AGE_BANDS = {
  primaryStartAge: 6,
  primaryEndAge: 13,
  highSchoolStartAge: 14,
  highSchoolEndAge: 17,
} as const;

export function defaultK12Fields(): Pick<
  Child529Plan,
  | 'primaryAnnualCostToday'
  | 'highSchoolAnnualCostToday'
  | 'primaryStartAge'
  | 'primaryEndAge'
  | 'highSchoolStartAge'
  | 'highSchoolEndAge'
> {
  return {
    primaryAnnualCostToday: 0,
    highSchoolAnnualCostToday: 0,
    ...DEFAULT_K12_AGE_BANDS,
  };
}

export const EMERGENCY_FUND_COVERAGE_MONTHS = 6;

export function emergencyFundFromMonthlyExpenses(monthlyLivingExpenses: number): number {
  return Math.max(0, monthlyLivingExpenses) * EMERGENCY_FUND_COVERAGE_MONTHS;
}

export function defaultBudgetCategories(): BudgetCategory[] {
  const items: Array<{ label: string; monthlyAmount: number }> = [
    { label: 'Housing (mortgage/rent)', monthlyAmount: 2400 },
    { label: 'Utilities & internet', monthlyAmount: 350 },
    { label: 'Groceries', monthlyAmount: 900 },
    { label: 'Transportation', monthlyAmount: 650 },
    { label: 'Insurance (home/auto/life)', monthlyAmount: 450 },
    { label: 'Childcare & kids', monthlyAmount: 800 },
    { label: 'Health & medical', monthlyAmount: 300 },
    { label: 'Dining out', monthlyAmount: 400 },
    { label: 'Subscriptions & personal', monthlyAmount: 250 },
    { label: 'Other / miscellaneous', monthlyAmount: 500 },
  ];
  return items.map((item) => ({ id: newBudgetCategoryId(), ...item }));
}

type BudgetCategory = import('../types/financialPlan').BudgetCategory;

function mergeBudgetCategories(
  raw: Partial<FinancialPlanData['currentFinances']> | undefined,
  fallbackTotal: number,
): BudgetCategory[] {
  if (Array.isArray(raw?.budgetCategories) && raw!.budgetCategories!.length > 0) {
    return raw!.budgetCategories!.map((c, i) => ({
      id: typeof c.id === 'string' ? c.id : newBudgetCategoryId(),
      label: typeof c.label === 'string' ? c.label : `Category ${i + 1}`,
      monthlyAmount:
        typeof c.monthlyAmount === 'number' && Number.isFinite(c.monthlyAmount)
          ? Math.max(0, c.monthlyAmount)
          : 0,
    }));
  }

  const total = typeof raw?.monthlyExpenses === 'number' ? raw.monthlyExpenses : fallbackTotal;
  return [{ id: newBudgetCategoryId(), label: 'Living expenses', monthlyAmount: Math.max(0, total) }];
}

function sumBudgetCategories(categories: BudgetCategory[]): number {
  return categories.reduce((s, c) => s + Math.max(0, c.monthlyAmount), 0);
}

/** Sum of budget category amounts only — excludes tithing (which is income-based). */
export function livingExpensesFromBudget(budgetCategories: BudgetCategory[]): number {
  return sumBudgetCategories(budgetCategories);
}

export const DEFAULT_PHASE_GROSS = 120000;

function incomePctToRate(pct: number): number {
  return Math.max(0, Math.min(100, pct)) / 100;
}

/** Gross income in today's dollars at a plan age (phase base + within-phase growth). */
export function grossIncomeTodayAtAge(
  profile: FinancialPlanProfile,
  income: FinancialPlanData['income'],
  age: number,
): number {
  const phase = income.phases.find((p) => age >= p.startAge && age <= p.endAge);
  if (!phase) return 0;
  const yearsInPhase = Math.max(0, age - phase.startAge);
  const growth = incomePctToRate(phase.withinPhaseGrowthPct ?? 0);
  const base = Math.max(0, phase.grossAnnualIncome) * Math.pow(1 + growth, yearsInPhase);
  return base + Math.max(0, phase.annualBonus ?? 0);
}

/** Display label for the income phase active at a plan age. */
export function incomePhaseLabelAtAge(
  income: FinancialPlanData['income'],
  age: number,
  retirementAge: number,
): string {
  if (age >= retirementAge) return 'Drawdown';
  const sorted = [...income.phases].sort((a, b) => a.startAge - b.startAge);
  const phase = sorted.find((p) => age >= p.startAge && age <= p.endAge);
  if (!phase) return 'Income gap';
  const trimmed = phase.label?.trim();
  if (trimmed) return trimmed;
  const index = sorted.indexOf(phase);
  return `Phase ${index + 1}`;
}

/** Nominal gross income at a plan age (today's dollars inflated from plan start). */
export function grossIncomeAtAge(
  profile: FinancialPlanProfile,
  income: FinancialPlanData['income'],
  age: number,
): number {
  const today = grossIncomeTodayAtAge(profile, income, age);
  const yearsFromNow = Math.max(0, age - profile.currentAge);
  const inflation = incomePctToRate(profile.inflationPct);
  return today * Math.pow(1 + inflation, yearsFromNow);
}

export function defaultIncomePhases(
  profile: FinancialPlanProfile,
  grossAnnualIncome = DEFAULT_PHASE_GROSS,
): IncomePhase[] {
  return [
    {
      id: newIncomePhaseId(),
      label: 'Working years',
      startAge: profile.currentAge,
      endAge: Math.max(profile.currentAge, profile.retirementAge - 1),
      grossAnnualIncome,
      annualBonus: 0,
      withinPhaseGrowthPct: 0,
    },
  ];
}

export function afterTaxMonthlyIncome(grossAnnualIncome: number, effectiveTaxRatePct: number): number {
  const taxRate = Math.min(100, Math.max(0, effectiveTaxRatePct)) / 100;
  return (Math.max(0, grossAnnualIncome) / 12) * (1 - taxRate);
}

export function monthlyTithingAmount(
  grossAnnualIncome: number,
  effectiveTaxRatePct: number,
  tithingPct: number,
): number {
  return afterTaxMonthlyIncome(grossAnnualIncome, effectiveTaxRatePct) * (Math.max(0, tithingPct) / 100);
}

export function computeMonthlyExpenses(
  budgetCategories: import('../types/financialPlan').BudgetCategory[],
  tithingPct: number,
  grossAnnualAtCurrentAge: number,
  effectiveTaxRatePct: number,
): number {
  return (
    sumBudgetCategories(budgetCategories) +
    monthlyTithingAmount(grossAnnualAtCurrentAge, effectiveTaxRatePct, tithingPct)
  );
}

export function syncCurrentFinancesExpenses(
  plan: FinancialPlanData,
): FinancialPlanData['currentFinances'] {
  const gross = grossIncomeTodayAtAge(plan.profile, plan.income, plan.profile.currentAge);
  return {
    ...plan.currentFinances,
    monthlyExpenses: computeMonthlyExpenses(
      plan.currentFinances.budgetCategories,
      plan.currentFinances.tithingPct,
      gross,
      plan.profile.effectiveTaxRatePct,
    ),
  };
}

/** Ensure at least one income phase; migrate legacy flat household gross when present. */
export function ensureIncomePhases(
  data: FinancialPlanData,
  legacyGrossAnnual?: number,
): FinancialPlanData {
  let next = data;
  if (data.income.phases.length === 0) {
    const gross =
      typeof legacyGrossAnnual === 'number' && legacyGrossAnnual > 0
        ? legacyGrossAnnual
        : DEFAULT_PHASE_GROSS;
    next = {
      ...data,
      income: { phases: defaultIncomePhases(data.profile, gross) },
    };
  }
  return { ...next, currentFinances: syncCurrentFinancesExpenses(next) };
}

type FinancialPlanCurrentFinances = import('../types/financialPlan').FinancialPlanCurrentFinances;

export function defaultRetirementSpendingCategories(): RetirementSpendingCategory[] {
  const items: Array<{ label: string; annualAmountToday: number }> = [
    { label: 'Housing', annualAmountToday: 36000 },
    { label: 'Food & groceries', annualAmountToday: 14400 },
    { label: 'Transportation', annualAmountToday: 12000 },
    { label: 'Healthcare', annualAmountToday: 9600 },
    { label: 'Insurance', annualAmountToday: 7200 },
    { label: 'Utilities', annualAmountToday: 6000 },
    { label: 'Entertainment & dining', annualAmountToday: 14400 },
    { label: 'Travel', annualAmountToday: 12000 },
    { label: 'Other', annualAmountToday: 8400 },
  ];
  return items.map((item) => ({ id: newSpendingCategoryId(), ...item }));
}

type RetirementSpendingCategory = import('../types/financialPlan').RetirementSpendingCategory;

function mergeSpendingCategories(
  raw: Partial<FinancialPlanData['retirement']> | undefined,
  fallbackTotal: number,
): RetirementSpendingCategory[] {
  if (Array.isArray(raw?.spendingCategories) && raw!.spendingCategories!.length > 0) {
    return raw!.spendingCategories!.map((c, i) => ({
      id: typeof c.id === 'string' ? c.id : newSpendingCategoryId(),
      label: typeof c.label === 'string' ? c.label : `Category ${i + 1}`,
      annualAmountToday:
        typeof c.annualAmountToday === 'number' && Number.isFinite(c.annualAmountToday)
          ? Math.max(0, c.annualAmountToday)
          : 0,
    }));
  }

  const total = typeof raw?.annualSpendingToday === 'number' ? raw.annualSpendingToday : fallbackTotal;
  return [{ id: newSpendingCategoryId(), label: 'Retirement spending', annualAmountToday: Math.max(0, total) }];
}

function sumSpendingCategories(categories: RetirementSpendingCategory[]): number {
  return categories.reduce((s, c) => s + Math.max(0, c.annualAmountToday), 0);
}

/** Keep annualSpendingToday aligned with category rows whenever categories are in use. */
export function syncRetirementSpending(
  retirement: FinancialPlanData['retirement'],
): FinancialPlanData['retirement'] {
  if (!retirement.spendingCategories?.length) return retirement;
  const annualSpendingToday = sumSpendingCategories(retirement.spendingCategories);
  if (annualSpendingToday === retirement.annualSpendingToday) return retirement;
  return { ...retirement, annualSpendingToday };
}

const defaultChildren = (): Child529Plan[] => [
  {
    id: newChildId(),
    name: 'Child 1',
    currentAge: 2,
    collegeStartAge: 18,
    costPreset: 'public_in_state',
    annualCost: COLLEGE_COST_PRESETS.public_in_state.annualCost,
    balance529: 5000,
    monthlyContribution529: 200,
    yearsOfCollege: 4,
    ...defaultK12Fields(),
  },
  {
    id: newChildId(),
    name: 'Child 2',
    currentAge: 4,
    collegeStartAge: 18,
    costPreset: 'public_in_state',
    annualCost: COLLEGE_COST_PRESETS.public_in_state.annualCost,
    balance529: 8000,
    monthlyContribution529: 200,
    yearsOfCollege: 4,
    ...defaultK12Fields(),
  },
];

/** Persona: savvy young husband, investing-focused */
export function defaultFinancialPlanData(): FinancialPlanData {
  const profile = {
    planStartDate: formatISODateLocal(new Date()),
    currentAge: 32,
    spouseAge: 30,
    retirementAge: 62,
    inflationPct: 3,
    portfolioReturnPct: 7,
    effectiveTaxRatePct: 24,
    safeWithdrawalRatePct: 4,
  };
  const budgetCategories = defaultBudgetCategories();
  const tithingPct = 10;
  const income = { phases: defaultIncomePhases(profile, DEFAULT_PHASE_GROSS) };
  const monthlyExpenses = computeMonthlyExpenses(
    budgetCategories,
    tithingPct,
    DEFAULT_PHASE_GROSS,
    profile.effectiveTaxRatePct,
  );
  const livingMonthly = livingExpensesFromBudget(budgetCategories);

  return {
    profile,
    currentFinances: {
      tithingPct,
      budgetCategories,
      autoEmergencyFund6Mo: true,
      monthlyExpenses,
      emergencyFundTarget: emergencyFundFromMonthlyExpenses(livingMonthly),
    },
    retirement: {
      mode: 'expenses',
      targetCorpus: 4000000,
      annualSpendingToday: 120000,
      yearsInRetirement: 30,
      spendingCategories: defaultRetirementSpendingCategories(),
    },
    children: defaultChildren(),
    health: {
      preMedicareMonthlyPremium: 1800,
      yearsOfCoverageNeeded: 3,
      hsaAnnualContribution: 8550,
      deductibleBufferTarget: 15000,
    },
    lifestyle: {
      vacationsPerYear: 2,
      vacationCost: 4000,
      otherGoals: [],
    },
    accounts: {
      k401: { balance: 85000, monthlyContribution: 1500, contributionPct: 10, annualReturnPct: 7 },
      rothIra: { balance: 45000, monthlyContribution: 583, contributionPct: 20, annualReturnPct: 7 },
      hsa: { balance: 12000, monthlyContribution: 712, contributionPct: 23, annualReturnPct: 7 },
      taxable: { balance: 12000, monthlyContribution: 200, contributionPct: 7, annualReturnPct: 7 },
    },
    accountSettings: { allocationMode: 'percent' },
    portfolio: {
      allocation: { usStocks: 55, intlStocks: 20, bonds: 15, alternatives: 10 },
    },
    income,
    debts: [],
  };
}

export function mergeFinancialPlanData(
  raw: Partial<FinancialPlanData> | null | undefined,
  options?: { preserveStored?: boolean },
): FinancialPlanData {
  const persona = defaultFinancialPlanData();
  const preserve = options?.preserveStored === true;
  if (!raw || typeof raw !== 'object') return persona;

  const num = (v: unknown, fallback: number) =>
    typeof v === 'number' && Number.isFinite(v) ? v : fallback;

  /** New optional fields default to off/zero so saved plans are not rewritten with persona values. */
  const optNum = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const optBool = (v: unknown) => v === true;

  type AccountInputs = import('../types/financialPlan').AccountInputs;

  const mergeAccount = (
    a: Partial<AccountInputs> | undefined,
    fallback: AccountInputs,
    portfolioReturnPct: number,
  ): AccountInputs => {
    if (!a || typeof a !== 'object') {
      return preserve
        ? { balance: 0, monthlyContribution: 0, contributionPct: 0, employerMatchPct: 0, annualReturnPct: portfolioReturnPct }
        : fallback;
    }
    return {
      balance: num(a.balance, preserve ? 0 : fallback.balance),
      monthlyContribution: num(a.monthlyContribution, preserve ? 0 : fallback.monthlyContribution),
      contributionPct: optNum(a.contributionPct) ?? fallback.contributionPct ?? 0,
      employerMatchPct: num(a.employerMatchPct ?? (preserve ? 0 : fallback.employerMatchPct), preserve ? 0 : fallback.employerMatchPct ?? 0),
      annualReturnPct: num(a.annualReturnPct ?? portfolioReturnPct, portfolioReturnPct),
    };
  };

  const mergeChild = (c: Partial<Child529Plan>, index: number): Child529Plan => {
    const base = persona.children[index] ?? {
      id: newChildId(),
      name: `Child ${index + 1}`,
      currentAge: 5,
      collegeStartAge: 18,
      costPreset: 'public_in_state' as CollegeCostPreset,
      annualCost: COLLEGE_COST_PRESETS.public_in_state.annualCost,
      balance529: 0,
      monthlyContribution529: 0,
      yearsOfCollege: 4,
      ...defaultK12Fields(),
    };
    const preset = (['public_in_state', 'public_out_state', 'private', 'custom'] as CollegeCostPreset[]).includes(
      c.costPreset as CollegeCostPreset,
    )
      ? (c.costPreset as CollegeCostPreset)
      : base.costPreset;
    return {
      id: typeof c.id === 'string' ? c.id : base.id,
      name: typeof c.name === 'string' ? c.name : base.name,
      birthDate:
        typeof c.birthDate === 'string' && isValidISODate(c.birthDate) ? c.birthDate : undefined,
      currentAge: num(c.currentAge, base.currentAge),
      collegeStartAge: num(c.collegeStartAge, base.collegeStartAge),
      costPreset: preset,
      annualCost: num(c.annualCost, base.annualCost),
      balance529: num(c.balance529, base.balance529),
      monthlyContribution529: num(c.monthlyContribution529, base.monthlyContribution529),
      yearsOfCollege: num(c.yearsOfCollege, base.yearsOfCollege),
      primaryAnnualCostToday: optNum(c.primaryAnnualCostToday),
      highSchoolAnnualCostToday: optNum(c.highSchoolAnnualCostToday),
      primaryStartAge: num(c.primaryStartAge, DEFAULT_K12_AGE_BANDS.primaryStartAge),
      primaryEndAge: num(c.primaryEndAge, DEFAULT_K12_AGE_BANDS.primaryEndAge),
      highSchoolStartAge: num(c.highSchoolStartAge, DEFAULT_K12_AGE_BANDS.highSchoolStartAge),
      highSchoolEndAge: num(c.highSchoolEndAge, DEFAULT_K12_AGE_BANDS.highSchoolEndAge),
    };
  };

  const mergeIncomePhase = (p: Partial<IncomePhase>, index: number, profile: FinancialPlanProfile): IncomePhase => {
    const base: IncomePhase = {
      id: newIncomePhaseId(),
      label: `Phase ${index + 1}`,
      startAge: profile.currentAge,
      endAge: Math.max(profile.currentAge, profile.retirementAge - 1),
      grossAnnualIncome: DEFAULT_PHASE_GROSS,
      annualBonus: 0,
      withinPhaseGrowthPct: 0,
    };
    return {
      id: typeof p.id === 'string' ? p.id : base.id,
      label: typeof p.label === 'string' ? p.label : base.label,
      startAge: num(p.startAge, base.startAge),
      endAge: num(p.endAge, base.endAge),
      grossAnnualIncome: num(p.grossAnnualIncome, base.grossAnnualIncome),
      annualBonus: optNum(p.annualBonus),
      withinPhaseGrowthPct: optNum(p.withinPhaseGrowthPct),
    };
  };

  const mergeDebt = (d: Partial<DebtObligation>, profileCurrentAge: number): DebtObligation => {
    const kind = (['mortgage', 'credit_card', 'personal', 'other'] as DebtKind[]).includes(
      d.kind as DebtKind,
    )
      ? (d.kind as DebtKind)
      : 'other';
    return {
      id: typeof d.id === 'string' ? d.id : newDebtId(),
      label: typeof d.label === 'string' ? d.label : 'Debt',
      kind,
      balance: optNum(d.balance),
      interestRatePct: optNum(d.interestRatePct),
      monthlyPayment: optNum(d.monthlyPayment),
      startAge: num(d.startAge, profileCurrentAge),
    };
  };

  const legacyGrossAnnual =
    raw.currentFinances &&
    typeof raw.currentFinances === 'object' &&
    typeof (raw.currentFinances as Record<string, unknown>).grossAnnualIncome === 'number'
      ? ((raw.currentFinances as Record<string, unknown>).grossAnnualIncome as number)
      : undefined;

  const mergedProfile =
    raw.profile && typeof raw.profile === 'object'
      ? {
          birthDate:
            typeof raw.profile.birthDate === 'string' && isValidISODate(raw.profile.birthDate)
              ? raw.profile.birthDate
              : undefined,
          spouseBirthDate:
            typeof raw.profile.spouseBirthDate === 'string' &&
            isValidISODate(raw.profile.spouseBirthDate)
              ? raw.profile.spouseBirthDate
              : undefined,
          planStartDate:
            typeof raw.profile.planStartDate === 'string' && isValidISODate(raw.profile.planStartDate)
              ? raw.profile.planStartDate
              : preserve
                ? undefined
                : persona.profile.planStartDate,
          currentAge: num(raw.profile.currentAge, preserve ? 35 : persona.profile.currentAge),
          spouseAge: num(raw.profile.spouseAge, preserve ? 35 : persona.profile.spouseAge),
          retirementAge: num(raw.profile.retirementAge, preserve ? 65 : persona.profile.retirementAge),
          inflationPct: num(raw.profile.inflationPct, preserve ? 3 : persona.profile.inflationPct),
          portfolioReturnPct: num(raw.profile.portfolioReturnPct, preserve ? 7 : persona.profile.portfolioReturnPct),
          effectiveTaxRatePct: num(raw.profile.effectiveTaxRatePct, preserve ? 24 : persona.profile.effectiveTaxRatePct),
          safeWithdrawalRatePct: num(raw.profile.safeWithdrawalRatePct, preserve ? 4 : persona.profile.safeWithdrawalRatePct),
        }
      : persona.profile;

  const merged: FinancialPlanData = {
    profile: mergedProfile,
    currentFinances: (() => {
      if (!raw.currentFinances || typeof raw.currentFinances !== 'object') return persona.currentFinances;
      const base = persona.currentFinances;
      const budgetCategories = mergeBudgetCategories(raw.currentFinances, preserve ? 0 : base.monthlyExpenses);
      const tithingPct = raw.currentFinances.tithingPct != null
        ? optNum(raw.currentFinances.tithingPct)
        : preserve
          ? 0
          : base.tithingPct;
      const autoEmergencyFund6Mo = optBool(raw.currentFinances.autoEmergencyFund6Mo);
      const livingMonthly = livingExpensesFromBudget(budgetCategories);
      const emergencyFundTarget = autoEmergencyFund6Mo
        ? emergencyFundFromMonthlyExpenses(livingMonthly)
        : num(raw.currentFinances.emergencyFundTarget, preserve ? 0 : base.emergencyFundTarget);
      return {
        tithingPct,
        monthlyExpenses: num(raw.currentFinances.monthlyExpenses, base.monthlyExpenses),
        autoEmergencyFund6Mo,
        emergencyFundTarget,
        budgetCategories,
      };
    })(),
    retirement: (() => {
      if (!raw.retirement || typeof raw.retirement !== 'object') return persona.retirement;
      const base = persona.retirement;
      const spendingCategories = mergeSpendingCategories(raw.retirement, preserve ? 0 : base.annualSpendingToday);
      const hasSpendingCategories = Array.isArray(raw.retirement.spendingCategories)
        && raw.retirement.spendingCategories.length > 0;
      const annualSpendingToday = hasSpendingCategories
        ? sumSpendingCategories(spendingCategories)
        : preserve && typeof raw.retirement.annualSpendingToday === 'number'
          ? raw.retirement.annualSpendingToday
          : num(raw.retirement.annualSpendingToday, sumSpendingCategories(spendingCategories));
      return syncRetirementSpending({
        mode: raw.retirement.mode === 'corpus' ? 'corpus' : 'expenses',
        targetCorpus: num(raw.retirement.targetCorpus, preserve ? 0 : base.targetCorpus),
        annualSpendingToday,
        yearsInRetirement: num(raw.retirement.yearsInRetirement, preserve ? 30 : base.yearsInRetirement),
        spendingCategories,
      });
    })(),
    children: Array.isArray(raw.children)
      ? raw.children.map((c, i) => mergeChild(c, i))
      : persona.children,
    health: raw.health && typeof raw.health === 'object'
      ? {
          preMedicareMonthlyPremium: num(raw.health.preMedicareMonthlyPremium, preserve ? 0 : persona.health.preMedicareMonthlyPremium),
          yearsOfCoverageNeeded: num(raw.health.yearsOfCoverageNeeded, preserve ? 0 : persona.health.yearsOfCoverageNeeded),
          hsaAnnualContribution: num(raw.health.hsaAnnualContribution, preserve ? 0 : persona.health.hsaAnnualContribution),
          deductibleBufferTarget: num(raw.health.deductibleBufferTarget, preserve ? 0 : persona.health.deductibleBufferTarget),
        }
      : persona.health,
    lifestyle: raw.lifestyle && typeof raw.lifestyle === 'object'
      ? {
          vacationsPerYear: num(raw.lifestyle.vacationsPerYear, preserve ? 0 : persona.lifestyle.vacationsPerYear),
          vacationCost: num(raw.lifestyle.vacationCost, preserve ? 0 : persona.lifestyle.vacationCost),
          otherGoals: Array.isArray(raw.lifestyle.otherGoals)
            ? raw.lifestyle.otherGoals.map((g, i) => ({
                id: typeof g.id === 'string' ? g.id : newGoalLineId(),
                label: typeof g.label === 'string' ? g.label : `Goal ${i + 1}`,
                annualAmount: num(g.annualAmount, 0),
              }))
            : (preserve ? [] : persona.lifestyle.otherGoals),
        }
      : persona.lifestyle,
    accounts: raw.accounts && typeof raw.accounts === 'object'
      ? {
          k401: mergeAccount(raw.accounts.k401, persona.accounts.k401, num(raw.profile?.portfolioReturnPct, persona.profile.portfolioReturnPct)),
          rothIra: mergeAccount(raw.accounts.rothIra, persona.accounts.rothIra, num(raw.profile?.portfolioReturnPct, persona.profile.portfolioReturnPct)),
          hsa: mergeAccount(raw.accounts.hsa, persona.accounts.hsa, num(raw.profile?.portfolioReturnPct, persona.profile.portfolioReturnPct)),
          taxable: mergeAccount(raw.accounts.taxable, persona.accounts.taxable, num(raw.profile?.portfolioReturnPct, persona.profile.portfolioReturnPct)),
        }
      : persona.accounts,
    portfolio: raw.portfolio?.allocation && typeof raw.portfolio.allocation === 'object'
      ? {
          allocation: {
            usStocks: num(raw.portfolio.allocation.usStocks, preserve ? 0 : persona.portfolio.allocation.usStocks),
            intlStocks: num(raw.portfolio.allocation.intlStocks, preserve ? 0 : persona.portfolio.allocation.intlStocks),
            bonds: num(raw.portfolio.allocation.bonds, preserve ? 0 : persona.portfolio.allocation.bonds),
            alternatives: num(raw.portfolio.allocation.alternatives, preserve ? 0 : persona.portfolio.allocation.alternatives),
          },
        }
      : persona.portfolio,
    income: raw.income && typeof raw.income === 'object'
      ? {
          phases: Array.isArray(raw.income.phases)
            ? raw.income.phases.map((p, i) => mergeIncomePhase(p, i, mergedProfile))
            : [],
        }
      : persona.income,
    debts: Array.isArray(raw.debts)
      ? raw.debts.map((d) =>
          mergeDebt(d, num(raw.profile?.currentAge, persona.profile.currentAge)),
        )
      : persona.debts,
    accountSettings:
      raw.accountSettings?.allocationMode === 'dollar'
        ? { allocationMode: 'dollar' }
        : { allocationMode: 'percent' },
  };

  const withPhases = ensureIncomePhases(merged, legacyGrossAnnual);

  if (withPhases.accountSettings.allocationMode === 'percent') {
    withPhases.accounts = initializeAccountContributionPercents(withPhases);
  }

  return resolvePlanAges(withPhases);
}

export function blendedReturnFromAllocation(allocation: FinancialPlanData['portfolio']['allocation']): number {
  const weights = {
    usStocks: 0.09,
    intlStocks: 0.08,
    bonds: 0.04,
    alternatives: 0.06,
  };
  const total = allocation.usStocks + allocation.intlStocks + allocation.bonds + allocation.alternatives || 100;
  return (
    (allocation.usStocks * weights.usStocks +
      allocation.intlStocks * weights.intlStocks +
      allocation.bonds * weights.bonds +
      allocation.alternatives * weights.alternatives) /
    total
  );
}
